import { type ReactNode, useEffect, useId, useRef } from 'react';

interface Props {
  open: boolean;
  title?: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  tone?: 'default' | 'danger';
  className?: string;
  variant?: 'modal' | 'drawer';
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  tone = 'default',
  className = '',
  variant = 'modal',
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onCancelOrClose = () => onClose();
    d.addEventListener('close', onCancelOrClose);
    // Light-dismiss fallback for browsers without closedBy (e.g. older Safari).
    let onClick: ((e: MouseEvent) => void) | undefined;
    if (!('closedBy' in HTMLDialogElement.prototype)) {
      onClick = (e) => {
        if (e.target !== d) return;
        const r = d.getBoundingClientRect();
        const inside =
          r.top <= e.clientY &&
          e.clientY <= r.top + r.height &&
          r.left <= e.clientX &&
          e.clientX <= r.left + r.width;
        if (!inside) d.close();
      };
      d.addEventListener('click', onClick);
    }
    return () => {
      d.removeEventListener('close', onCancelOrClose);
      if (onClick) d.removeEventListener('click', onClick);
    };
  }, [onClose]);

  // Keep <dialog> mounted always; gate only the CONTENT on `open`. Unmounting on
  // close would skip the native close procedure and lose focus restoration (a11y).
  return (
    <dialog
      ref={ref}
      closedby="any"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
      className={`bo-card overflow-hidden p-0 shadow-modal backdrop:bg-ink/40 open:flex open:flex-col ${
        variant === 'drawer'
          ? 'm-0 ml-auto h-dvh max-h-dvh w-full max-w-[600px] rounded-none border-0'
          : 'm-auto w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] sm:w-[min(560px,92vw)] sm:max-h-[88vh]'
      } ${tone === 'danger' ? 'border-t-4 border-t-danger' : ''} ${className}`}
    >
      {open && (
        <>
          {title && (
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line-2 bg-surface-2/30 p-4 sm:px-5">
              <div className="min-w-0">
                <h3
                  id={titleId}
                  className="font-display text-h3 font-semibold text-ink"
                >
                  {title}
                </h3>
                {description ? (
                  <p id={descriptionId} className="mt-1 text-body text-ink-3">
                    {description}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                aria-label="ปิดหน้าต่าง"
                className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-btn text-ink-3 transition-colors hover:bg-bg-warm hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
                onClick={onClose}
              >
                ✕
              </button>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
            {children}
          </div>
          {footer ? (
            <div className="shrink-0 border-t border-line-2 bg-surface-2/30 p-4 sm:px-5">
              {footer}
            </div>
          ) : null}
        </>
      )}
    </dialog>
  );
}
